import { ExternalLink, FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  OrderWorkflowStage,
  QcSubmission,
  StageRun,
  WorkflowFile,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';

function FileLink({ file }: { file: WorkflowFile }) {
  const { t } = useTranslation('production');
  const name = file.alt ?? t('media.untitled');
  const image = file.mimeType?.startsWith('image/') && file.url;
  return (
    <a
      href={file.url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="hover:bg-muted flex items-center gap-2 rounded-md border p-1.5 pr-2 text-xs"
    >
      {image ? (
        <img src={file.url ?? undefined} alt="" className="size-8 rounded object-cover" />
      ) : (
        <FileText className="text-muted-foreground size-4" aria-hidden />
      )}
      <span className="max-w-40 truncate">{name}</span>
    </a>
  );
}

export function FilesList({ files, label }: { files: WorkflowFile[]; label: string }) {
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" aria-label={label}>
      {files.map((file) => (
        <li key={file.mediaId}>
          <FileLink file={file} />
        </li>
      ))}
    </ul>
  );
}

export function NotesList({ run, name }: { run: StageRun; name: string }) {
  const { t } = useTranslation('production');
  const format = useFormat();
  if (run.notes.length === 0) return null;
  return (
    <ol
      className="border-muted flex flex-col gap-2 border-l-2 pl-3"
      aria-label={t('stage.notes', { name })}
    >
      {run.notes.map((note) => (
        <li key={note.id} className="text-sm">
          <p className="whitespace-pre-line">{note.body}</p>
          <p className="text-muted-foreground text-xs" title={format.utc(note.createdAt)}>
            {t('stage.noteBy', {
              name: note.author?.name ?? t('stage.someone'),
              when: format.relative(note.createdAt),
            })}
          </p>
        </li>
      ))}
    </ol>
  );
}

function answerText(
  value: boolean | number | string | null | undefined,
  yes: string,
  no: string,
): string {
  if (value === true) return yes;
  if (value === false) return no;
  if (value === null || value === undefined) return '—';
  return String(value);
}

function SubmissionItem({
  stage,
  submission,
}: {
  stage: OrderWorkflowStage;
  submission: QcSubmission;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const [open, setOpen] = useState(false);
  return (
    <li className="flex flex-col gap-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span title={format.utc(submission.submittedAt)}>
          {t('qc.submission', {
            name: submission.submittedBy?.name ?? t('stage.someone'),
            when: format.relative(submission.submittedAt),
          })}
        </span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto px-0"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? t('qc.hideAnswers') : t('qc.showAnswers')}
        </Button>
      </div>
      {open ? (
        <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-xs">
          {stage.qcFields
            .filter((field) => field.type !== 'file')
            .map((field) => (
              <div key={field.key} className="contents">
                <dt className="text-muted-foreground">{field.label}</dt>
                <dd className="text-right">
                  {answerText(submission.answers[field.key], t('qc.yes'), t('qc.no'))}
                </dd>
              </div>
            ))}
        </dl>
      ) : null}
      <FilesList files={submission.files} label={t('stage.files', { name: stage.name })} />
    </li>
  );
}

export function QcSubmissionsList({ stage }: { stage: OrderWorkflowStage }) {
  const { t } = useTranslation('production');
  const submissions = stage.run.qcSubmissions;
  if (submissions.length === 0) return null;
  return (
    <ol className="flex flex-col gap-3" aria-label={t('qc.submissions', { name: stage.name })}>
      {[...submissions].reverse().map((submission) => (
        <SubmissionItem key={submission.id} stage={stage} submission={submission} />
      ))}
    </ol>
  );
}

export function AttemptsList({ stage }: { stage: OrderWorkflowStage }) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const attempts = stage.run.attempts;
  if (attempts.length === 0) return null;
  return (
    <ol className="flex flex-col gap-3" aria-label={t('attempts.title', { name: stage.name })}>
      {[...attempts].reverse().map((attempt) => {
        const response = attempt.response;
        const answer = !response
          ? attempt.attempt === stage.run.attempt && stage.run.status === 'waiting_customer'
            ? t('attempts.waiting')
            : t('attempts.closed')
          : response.action === 'request_changes'
            ? t('attempts.changes', {
                when: format.relative(response.respondedAt),
                message: response.message ?? '',
              })
            : t('attempts.approved', { when: format.relative(response.respondedAt) });
        return (
          <li
            key={attempt.attempt}
            aria-label={t('attempts.attempt', { n: attempt.attempt })}
            className="flex flex-col gap-1.5 rounded-md border p-2 text-sm"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{t('attempts.attempt', { n: attempt.attempt })}</span>
              <span className="text-muted-foreground text-xs" title={format.utc(attempt.sentAt)}>
                {t('attempts.sent', { when: format.relative(attempt.sentAt) })}
              </span>
              {attempt.respondBy && !response ? (
                <span
                  className="text-muted-foreground text-xs"
                  title={format.utc(attempt.respondBy)}
                >
                  {t('attempts.respondBy', { when: format.dateTime(attempt.respondBy) })}
                </span>
              ) : null}
            </div>
            {attempt.message ? (
              <p className="text-muted-foreground text-xs">{attempt.message}</p>
            ) : null}
            <FilesList files={attempt.files} label={t('stage.files', { name: stage.name })} />
            <p
              className={
                response?.action === 'request_changes' ? 'text-foreground' : 'text-muted-foreground'
              }
            >
              {answer}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export function ParcelsList({
  stage,
  lines = [],
}: {
  stage: OrderWorkflowStage;
  /** The order's lines, to name what each parcel carried. */
  lines?: ReadonlyArray<{ id: string; title: string }>;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const parcels = stage.run.parcels;
  if (parcels.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2" aria-label={t('parcels.list', { name: stage.name })}>
      {parcels.map((parcel) => (
        <li
          key={parcel.key}
          aria-label={parcel.label}
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          <span className="font-medium">{parcel.label}</span>
          {parcel.carriesItems ? (
            <Badge variant="secondary">{t('parcels.carriesItems')}</Badge>
          ) : null}
          {parcel.carrier && parcel.trackingNumber ? (
            <>
              <span>
                {t('parcels.shipped', { carrier: parcel.carrier, number: parcel.trackingNumber })}
              </span>
              {parcel.trackingUrl ? (
                <a
                  href={parcel.trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 underline underline-offset-2"
                >
                  {t('parcels.track')}
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              ) : null}
              {parcel.shippedAt ? (
                <span
                  className="text-muted-foreground text-xs"
                  title={format.utc(parcel.shippedAt)}
                >
                  {t('parcels.shippedAt', { when: format.relative(parcel.shippedAt) })}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-muted-foreground">{t('parcels.notShipped')}</span>
          )}
          {parcel.lines.length > 0 ? (
            <span className="text-muted-foreground w-full text-xs">
              {parcel.lines
                .map(
                  (item) =>
                    `${item.quantity} × ${lines.find((l) => l.id === item.lineId)?.title ?? t('parcels.unknownLine')}`,
                )
                .join(', ')}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function CustomerAnswer({ run }: { run: StageRun }) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const response = run.customerResponse;
  if (!response) return null;
  const text =
    response.action === 'confirm'
      ? t('stage.customerConfirmed')
      : response.action === 'approve'
        ? t('stage.customerApproved')
        : t('stage.customerRequestedChanges', { message: response.message ?? '' });
  return (
    <p className="text-sm">
      <span className="text-muted-foreground" title={format.utc(response.respondedAt)}>
        {t('stage.customerAnswer', { when: format.relative(response.respondedAt) })}:{' '}
      </span>
      {text}
    </p>
  );
}
